import React, { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    apiListLeaves,
    apiCreateLeave,
    apiUpdateLeaveStatus,
    apiCancelLeave,
    apiListUsers,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Plane, Check, X, Plus, AlertCircle, Trash2 } from "lucide-react";

const MANAGE_ROLES = new Set(["Super Admin", "Admin", "Team Lead"]);

export default function Leaves() {
    const { user } = useAuth();
    const canManage = MANAGE_ROLES.has(user?.role);
    const [leaves, setLeaves] = useState([]);
    const [userDir, setUserDir] = useState({});
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await apiListLeaves();
            setLeaves(list);
            if (canManage) {
                try {
                    const users = await apiListUsers();
                    const dir = {};
                    users.forEach((u) => (dir[u.user_id] = u));
                    setUserDir(dir);
                } catch {
                    /* Team Leads may not have access to /api/users; fall back to name-less */
                    setUserDir({});
                }
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [canManage]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        if (filter === "all") return leaves;
        return leaves.filter((l) => l.status === filter);
    }, [leaves, filter]);

    async function decide(l, status) {
        try {
            const res = await apiUpdateLeaveStatus(l.leave_id, status);
            if (res.warning) toast.warning(res.warning);
            else toast.success(`Leave ${status.toLowerCase()}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function cancel(l) {
        if (!window.confirm("Cancel this leave request?")) return;
        try {
            await apiCancelLeave(l.leave_id);
            toast.success("Leave cancelled");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <Layout active="leaves">
            <PageHeader
                title="Leaves"
                subtitle={
                    canManage
                        ? "Approve or reject leave requests. Approved leave blocks roster conflicts."
                        : "Submit and track your leave requests."
                }
                actions={
                    <>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger
                                className="h-9 w-[160px]"
                                data-testid="leaves-filter-trigger"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="Requested">Requested</SelectItem>
                                <SelectItem value="Approved">Approved</SelectItem>
                                <SelectItem value="Rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={() => setDialogOpen(true)}
                            data-testid="request-leave-btn"
                            className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            <Plus className="h-4 w-4 mr-1.5" />
                            Request leave
                        </Button>
                    </>
                }
            />

            <div
                className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                data-testid="leaves-table"
            >
                <div className="grid grid-cols-[minmax(180px,1.5fr)_minmax(220px,1.8fr)_110px_110px_minmax(180px,2fr)_160px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                    <div>User</div>
                    <div>Dates</div>
                    <div>Type</div>
                    <div>Status</div>
                    <div>Reason</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">
                        Loading leaves…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-ntt-muted">
                        No leaves to show.
                    </div>
                ) : (
                    filtered.map((l) => {
                        const isOwn = l.user_id === user?.user_id;
                        const u = userDir[l.user_id];
                        const canDecide = canManage && l.status === "Requested";
                        const canCancel =
                            (isOwn && l.status === "Requested") ||
                            (canManage && l.status !== "Requested");
                        return (
                            <div
                                key={l.leave_id}
                                data-testid={`leave-row-${l.leave_id}`}
                                className="grid grid-cols-[minmax(180px,1.5fr)_minmax(220px,1.8fr)_110px_110px_minmax(180px,2fr)_160px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                            >
                                <div className="text-[13px] text-[#070F26] truncate">
                                    {isOwn ? "You" : u?.full_name || l.user_id}
                                </div>
                                <div className="text-[13px] font-mono text-[#2B2F3A]">
                                    {l.start_date} → {l.end_date}
                                </div>
                                <div>
                                    <StatusPill
                                        label={l.type}
                                        tone={l.type === "Planned" ? "blue" : "amber"}
                                    />
                                </div>
                                <div>
                                    <StatusPill
                                        label={l.status}
                                        tone={
                                            l.status === "Approved"
                                                ? "green"
                                                : l.status === "Rejected"
                                                  ? "red"
                                                  : "gray"
                                        }
                                    />
                                </div>
                                <div className="text-[12px] text-ntt-muted truncate">
                                    {l.reason || "—"}
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    {canDecide && (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => decide(l, "Approved")}
                                                data-testid={`approve-${l.leave_id}`}
                                                className="h-8 text-[12px] text-emerald-700 hover:text-emerald-800"
                                            >
                                                <Check className="h-3.5 w-3.5 mr-1" />
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => decide(l, "Rejected")}
                                                data-testid={`reject-${l.leave_id}`}
                                                className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                            >
                                                <X className="h-3.5 w-3.5 mr-1" />
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    {canCancel && !canDecide && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => cancel(l)}
                                            data-testid={`cancel-${l.leave_id}`}
                                            className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <RequestLeaveDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                onDone={load}
            />
        </Layout>
    );
}

function RequestLeaveDialog({ open, onOpenChange, onDone }) {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [type, setType] = useState("Planned");
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setStart("");
            setEnd("");
            setType("Planned");
            setReason("");
            setError("");
        }
    }, [open]);

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!start || !end) {
            setError("Both start and end dates are required.");
            return;
        }
        if (end < start) {
            setError("End date must be on or after start date.");
            return;
        }
        setSaving(true);
        try {
            await apiCreateLeave({
                start_date: start,
                end_date: end,
                type,
                reason: reason || undefined,
            });
            toast.success("Leave submitted");
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]" data-testid="leave-dialog">
                <DialogHeader>
                    <DialogTitle>Request leave</DialogTitle>
                    <DialogDescription>
                        Once approved, the roster will block any conflicting
                        assignments for you.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="ls" className="text-[12px] text-ntt-muted mb-1.5 block">
                                Start date
                            </Label>
                            <Input
                                id="ls"
                                type="date"
                                required
                                value={start}
                                onChange={(e) => setStart(e.target.value)}
                                data-testid="leave-start-input"
                                className="h-10 font-mono"
                            />
                        </div>
                        <div>
                            <Label htmlFor="le" className="text-[12px] text-ntt-muted mb-1.5 block">
                                End date
                            </Label>
                            <Input
                                id="le"
                                type="date"
                                required
                                value={end}
                                onChange={(e) => setEnd(e.target.value)}
                                data-testid="leave-end-input"
                                className="h-10 font-mono"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Type
                        </Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="h-10" data-testid="leave-type-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Planned" data-testid="leave-type-planned">
                                    Planned
                                </SelectItem>
                                <SelectItem value="Unplanned" data-testid="leave-type-unplanned">
                                    Unplanned
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="lr" className="text-[12px] text-ntt-muted mb-1.5 block">
                            Reason (optional)
                        </Label>
                        <Input
                            id="lr"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Family vacation"
                            data-testid="leave-reason-input"
                            className="h-10"
                        />
                    </div>
                    {error && (
                        <div
                            data-testid="leave-form-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="leave-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="leave-submit-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            <Plane className="h-4 w-4 mr-1.5" />
                            {saving ? "Submitting…" : "Submit request"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
